import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Database, Cloud, AlertTriangle } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';

interface StorageConfig {
  kind: 'PG' | 'S3';
  s3Endpoint: string | null;
  s3Region: string | null;
  s3Bucket: string | null;
  s3PathStyle: boolean;
  migrationState: 'IDLE' | 'MIGRATING' | 'READY';
}

const PRESETS: Record<string, { endpoint: string; pathStyle: boolean; help: string }> = {
  AWS: { endpoint: '', pathStyle: false, help: 'Leave the endpoint blank for native AWS S3.' },
  R2: {
    endpoint: 'https://<account-id>.r2.cloudflarestorage.com',
    pathStyle: false,
    help: 'Cloudflare R2. Replace <account-id>. Use auto for region.',
  },
  MinIO: {
    endpoint: 'http://minio.local:9000',
    pathStyle: true,
    help: 'Self-hosted MinIO. Path-style addressing required.',
  },
  Backblaze: {
    endpoint: 'https://s3.us-east-005.backblazeb2.com',
    pathStyle: false,
    help: 'Backblaze B2. Endpoint depends on the bucket region.',
  },
  Wasabi: {
    endpoint: 'https://s3.us-east-1.wasabisys.com',
    pathStyle: false,
    help: 'Wasabi. Endpoint depends on the storage region.',
  },
};

/**
 * Epic 88, Story 88.5 — admin page for switching attachment storage
 * to an S3-compatible backend.
 *
 * Once a tenant flips here, new uploads go to S3 immediately.
 * Existing PG-resident blobs migrate lazily via the cron job
 * (AttachmentMigrationCronService); the migrationState badge surfaces
 * progress.
 */
export function AttachmentStorageAdminPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';

  const [config, setConfig] = useState<StorageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [preset, setPreset] = useState<string>('AWS');
  const [endpoint, setEndpoint] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [bucket, setBucket] = useState('');
  const [pathStyle, setPathStyle] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');

  function load() {
    setLoading(true);
    apiClient
      .get<StorageConfig>('/admin/attachment-storage')
      .then(({ data }) => {
        setConfig(data);
        if (data.kind === 'S3') {
          setEndpoint(data.s3Endpoint ?? '');
          setRegion(data.s3Region ?? '');
          setBucket(data.s3Bucket ?? '');
          setPathStyle(data.s3PathStyle);
        }
      })
      .catch(() => setConfig(null))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function applyPreset(p: string) {
    setPreset(p);
    const cfg = PRESETS[p];
    if (cfg) {
      setEndpoint(cfg.endpoint);
      setPathStyle(cfg.pathStyle);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await apiClient.post('/admin/attachment-storage/s3', {
        endpointUrl: endpoint.trim() || null,
        region: region.trim(),
        bucket: bucket.trim(),
        pathStyle,
        accessKey: accessKey.trim(),
        secretKey: secretKey.trim(),
      });
      setSuccess(t('attachmentStorage.saved'));
      setAccessKey('');
      setSecretKey('');
      load();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('attachmentStorage.error_save'));
    } finally {
      setSaving(false);
    }
  }

  if (!isAdmin) return <p className="text-sm text-destructive">{t('common.access_denied')}</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Cloud className="h-5 w-5 text-primary" />
          {t('attachmentStorage.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('attachmentStorage.subtitle')}
        </p>
      </div>

      {/* Current state */}
      {!loading && config && (
        <section className="rounded-lg border bg-card p-4">
          <h3 className="mb-2 flex items-center gap-2 font-medium">
            {config.kind === 'PG' ? (
              <Database className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Cloud className="h-4 w-4 text-primary" />
            )}
            {t('attachmentStorage.current_state')}
          </h3>
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">{t('attachmentStorage.backend')}:</span>{' '}
              <span className="font-medium">
                {config.kind === 'PG' ? t('attachmentStorage.pg_label') : t('attachmentStorage.s3_label')}
              </span>
            </div>
            {config.kind === 'S3' && (
              <>
                <div>
                  <span className="text-muted-foreground">{t('attachmentStorage.endpoint')}:</span>{' '}
                  <code className="text-xs">{config.s3Endpoint ?? 'AWS default'}</code>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('attachmentStorage.region')}:</span>{' '}
                  <code className="text-xs">{config.s3Region}</code>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('attachmentStorage.bucket')}:</span>{' '}
                  <code className="text-xs">{config.s3Bucket}</code>
                </div>
                <div className="mt-2">
                  <span className="text-muted-foreground">{t('attachmentStorage.migration_state')}:</span>{' '}
                  <span
                    className={
                      config.migrationState === 'READY'
                        ? 'rounded bg-green-900/40 px-2 py-0.5 text-xs font-medium text-green-300'
                        : config.migrationState === 'MIGRATING'
                          ? 'rounded bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-300'
                          : 'rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
                    }
                  >
                    {config.migrationState}
                  </span>
                  {config.migrationState !== 'READY' && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('attachmentStorage.migration_explainer')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Form */}
      <section className="rounded-lg border bg-card p-4">
        <h3 className="mb-3 font-medium">
          {config?.kind === 'S3' ? t('attachmentStorage.update_form') : t('attachmentStorage.activate_form')}
        </h3>
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-800 bg-amber-950/40 p-3 text-sm text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            {t('attachmentStorage.warning_credentials')}{' '}
            <strong>{t('attachmentStorage.warning_credentials_emphasis')}</strong>
          </div>
        </div>
        {error && (
          <div className="mb-3 rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 rounded-md border border-green-800 bg-green-950/40 px-3 py-2 text-sm text-green-300">
            {success}
          </div>
        )}
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('attachmentStorage.preset')}</label>
            <div className="flex gap-1">
              {Object.keys(PRESETS).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={
                    'rounded-md border px-3 py-1.5 text-xs ' +
                    (preset === p
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'hover:bg-accent')
                  }
                >
                  {p}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{PRESETS[preset]?.help}</p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">{t('attachmentStorage.endpoint')}</label>
            <Input
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="(blank for AWS default)"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">{t('attachmentStorage.region')}</label>
              <Input
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="us-east-1"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">{t('attachmentStorage.bucket')}</label>
              <Input
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
                placeholder="my-tenant-attachments"
                required
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="path-style"
              type="checkbox"
              checked={pathStyle}
              onChange={(e) => setPathStyle(e.target.checked)}
            />
            <label htmlFor="path-style" className="text-sm">
              {t('attachmentStorage.path_style')}
            </label>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">{t('attachmentStorage.access_key')}</label>
            <Input
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              required
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">{t('attachmentStorage.secret_key')}</label>
            <Input
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              required
              autoComplete="off"
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" size="sm" disabled={saving}>
              {saving
                ? t('common.saving')
                : config?.kind === 'S3'
                  ? t('attachmentStorage.update')
                  : t('attachmentStorage.activate')}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
